/** @typedef {import('./types').HostKind} HostKind */
/** @typedef {import('./types').MailItemSnapshot} MailItemSnapshot */

const BRIDGE_VERSION = 1;
const RPC_TIMEOUT_MS = 30000;

let requestCounter = 0;

const nextRequestId = () => `rpc-${Date.now()}-${++requestCounter}`;

/**
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

/**
 * WebView2 may deliver postMessage payloads as objects or JSON strings.
 * @param {MessageEvent} event
 * @returns {Record<string, unknown> | null}
 */
const parseMessageData = (event) => {
  let data = event?.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") {
    return null;
  }
  return /** @type {Record<string, unknown>} */ (data);
};

/**
 * @param {string} method
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<unknown>}
 */
const callBridge = (method, params = {}) => {
  const webview = window.chrome?.webview;
  if (!webview?.postMessage) {
    return Promise.reject(new Error("VSTO bridge unavailable: window.chrome.webview not found"));
  }

  const id = nextRequestId();

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      webview.removeEventListener("message", onMessage);
      reject(new Error(`VSTO bridge RPC timeout: ${method}`));
    }, RPC_TIMEOUT_MS);

    const onMessage = (event) => {
      const data = event.data;
      if (!data || data.id !== id) {
        return;
      }
      clearTimeout(timeout);
      webview.removeEventListener("message", onMessage);
      if (data.error) {
        reject(new Error(data.error.message || data.error.code || "Bridge RPC failed"));
      } else {
        resolve(data.result);
      }
    };

    webview.addEventListener("message", onMessage);
    webview.postMessage({ id, method, params });
  });
};

/** @type {MailItemSnapshot | null} */
let currentSnapshot = null;

/** @type {Set<(snapshot: MailItemSnapshot) => void>} */
const itemChangedListeners = new Set();

const handleBridgeEvent = (event) => {
  const data = parseMessageData(event);
  if (!data?.type) {
    return;
  }
  if (data.type === "itemChanged" && data.payload) {
    currentSnapshot = data.payload;
    itemChangedListeners.forEach((listener) => listener(currentSnapshot));
  }
  if (data.type === "ready" && data.payload) {
    currentSnapshot = data.payload;
  }
};

/**
 * @returns {import('./officeJsHost').OutlookHost}
 */
export const createVstoBridgeHost = () => {
  const webview = window.chrome?.webview;
  if (webview?.addEventListener) {
    webview.addEventListener("message", handleBridgeEvent);
  }

  return {
    kind: /** @type {HostKind} */ ("vsto"),
    bridgeVersion: BRIDGE_VERSION,

    async onReady() {
      const result = await callBridge("onReady");
      if (result) {
        currentSnapshot = /** @type {MailItemSnapshot} */ (result);
      }
    },

    onItemChanged(handler) {
      itemChangedListeners.add(handler);
      return () => itemChangedListeners.delete(handler);
    },

    async waitForItemReady() {
      const result = await callBridge("waitForItemReady");
      currentSnapshot = /** @type {MailItemSnapshot} */ (result);
      return currentSnapshot;
    },

    getSnapshot() {
      return currentSnapshot;
    },

    isComposeMode() {
      return !!currentSnapshot?.isCompose;
    },

    getUserDisplayName() {
      return currentSnapshot?.userDisplayName || "";
    },

    getMessageKey() {
      if (currentSnapshot?.isCompose) {
        return currentSnapshot?.inReplyTo || currentSnapshot?.internetMessageId || currentSnapshot?.conversationId || "";
      }
      return currentSnapshot?.internetMessageId || "";
    },

    async getSubject() {
      return /** @type {string} */ (await callBridge("getSubject"));
    },

    async setSubject(subject) {
      await callBridge("setSubject", { subject });
      if (currentSnapshot) {
        currentSnapshot = { ...currentSnapshot, subject };
      }
    },

    async getBodyText(caller) {
      return /** @type {string} */ (await callBridge("getBodyText", { caller }));
    },

    async getBodyHtml(caller) {
      return /** @type {string} */ (await callBridge("getBodyHtml", { caller }));
    },

    async getBodyType(caller) {
      return /** @type {string} */ (await callBridge("getBodyType", { caller }));
    },

    async setBody(content) {
      await callBridge("setBody", { content });
    },

    async getSelectedText() {
      const result = await callBridge("getSelectedText");
      if (typeof result === "string") {
        return { data: result };
      }
      return result || { data: "" };
    },

    async getRecipients() {
      return /** @type {{ sender: object, to: object[], cc: object[] }} */ (
        await callBridge("getRecipients")
      );
    },

    async attachFileFromUrl(url, filename) {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download attachment (${response.status})`);
      }

      const buffer = await response.arrayBuffer();
      const data = arrayBufferToBase64(buffer);
      await callBridge("attachFileFromData", { data, filename });
    },

    openReplyForm(content) {
      callBridge("openReplyForm", { content }).catch(console.error);
    },

    openReplyAllForm(content) {
      callBridge("openReplyAllForm", { content }).catch(console.error);
    },

    async getConversationThread() {
      const result = await callBridge("getConversationThread");
      return result || [];
    },

    isMessageItem() {
      return currentSnapshot?.itemType === "message" || currentSnapshot?.itemType === "ipm.note";
    },

    supportsGraph() {
      return false;
    },

    dispose() {
      itemChangedListeners.clear();
    },
  };
};
